import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API hình ảnh khách hàng (gallery/ghi chú ảnh).
 *  - getCustomerPhotos:   GET    /api/v1/be/customers/{id}/photos
 *  - uploadCustomerPhoto: POST   /api/v1/be/customers/{id}/photos        (multipart: image, ≤5MB)
 *  - deleteCustomerPhoto: DELETE /api/v1/be/customers/photos/{photoId}
 */

export interface CustomerPhoto {
    id: number;
    customer_id: number;
    image: string;
    created_at?: string;
    updated_at?: string;
}

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export async function getCustomerPhotos(
    customerId: number,
    params: { page?: number; limit?: number } = {}
): Promise<{ status: boolean; items: CustomerPhoto[]; total: number }> {
    try {
        const q = new URLSearchParams();
        if (params.page) q.append("page", String(params.page));
        if (params.limit) q.append("limit", String(params.limit));
        const res = await fetch(`${API_URL}/api/v1/be/customers/${customerId}/photos?${q.toString()}`, {
            headers: authHeaders(),
        });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        // API có thể trả data là mảng trực tiếp hoặc { items, total }
        const data = json?.data;
        const items: CustomerPhoto[] = Array.isArray(data)
            ? data
            : Array.isArray(data?.items)
            ? data.items
            : [];
        const total = Array.isArray(data) ? items.length : data?.total ?? items.length;
        return { status: !!json?.status, items, total };
    } catch (error) {
        console.error("Failed to fetch customer photos:", error);
        return { status: false, items: [], total: 0 };
    }
}

export async function uploadCustomerPhoto(
    customerId: number,
    file: File
): Promise<{ status: boolean; data: CustomerPhoto | null; msg?: string }> {
    try {
        const form = new FormData();
        // API yêu cầu mảng files dưới tên photos[] (không phải "image")
        form.append("photos[]", file);
        const res = await fetch(`${API_URL}/api/v1/be/customers/${customerId}/photos`, {
            method: "POST",
            headers: authHeaders(), // không set Content-Type để browser tự thêm boundary
            body: form,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            return { status: false, data: null, msg: json?.msg || `HTTP ${res.status}` };
        }
        // data có thể là object đơn hoặc mảng (lấy phần tử đầu)
        const d = json?.data;
        const item = Array.isArray(d) ? d[0] : d;
        const errMsg = json?.error ? Object.values(json.error)[0] : json?.msg;
        return { status: !!json?.status, data: item ?? null, msg: errMsg as string };
    } catch (error) {
        console.error("Failed to upload customer photo:", error);
        return { status: false, data: null, msg: "Tải lên thất bại" };
    }
}

export async function deleteCustomerPhoto(
    photoId: number
): Promise<{ status: boolean; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/customers/photos/${photoId}`, {
            method: "DELETE",
            headers: authHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            return { status: false, msg: json?.msg || `HTTP ${res.status}` };
        }
        return { status: json?.status ?? true, msg: json?.msg };
    } catch (error) {
        console.error("Failed to delete customer photo:", error);
        return { status: false, msg: "Xóa thất bại" };
    }
}
